/**
 * imageUploadHelper.js
 * Universal mobile & iOS-compatible image upload and normalization utility.
 * Handles iOS HEIC conversion, client-side downscaling, EXIF normalization,
 * and reliable file uploads without exceeding mobile memory/localStorage limits.
 */
import axios from 'axios';
import { getApiUrl, authHeaders, getTenantStorageKey } from './styleHelpers.js';

/**
 * Standard CSS style for hidden file inputs that avoids iOS Safari <label> tap bugs.
 * iOS Safari ignores taps on <label> when the child <input type="file"> has display: none.
 */
export const IOS_FILE_INPUT_STYLE = {
    position: 'absolute',
    opacity: 0,
    width: '1px',
    height: '1px',
    pointerEvents: 'none',
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    border: 0,
    padding: 0,
    margin: '-1px'
};

/**
 * Checks if a file is an image based on MIME type or filename extension.
 */
export const isImageFile = (file) => {
    if (!file) return false;
    const type = (file.type || '').toLowerCase();
    if (type.startsWith('image/')) return true;
    const name = (file.name || '').toLowerCase();
    return /\.(jpe?g|png|webp|gif|avif|heic|heif|jfif|bmp|svg)$/i.test(name);
};

/**
 * Converts an image file (including iOS HEIC if browser decodes it, or standard camera JPGs)
 * into an optimized JPEG/WebP Blob and Data URL using an offscreen HTML5 Canvas.
 * Limits max dimensions (default 2560px) and compresses to ~85% quality.
 */
export const normalizeAndCompressImage = async (file, options = {}) => {
    const {
        maxDimension = 2560,
        quality = 0.88,
        preferredMime = 'image/jpeg'
    } = options;

    if (!isImageFile(file)) {
        return { file, dataUrl: null };
    }

    // Attempt canvas normalization
    try {
        let bitmap = null;
        if (typeof createImageBitmap === 'function') {
            try {
                bitmap = await createImageBitmap(file);
            } catch (err) {
                // Fall back to Image loading if createImageBitmap fails on specific format
                bitmap = null;
            }
        }

        let width = 0;
        let height = 0;
        let drawSource = null;
        let objectUrlToRevoke = null;

        if (bitmap) {
            width = bitmap.width;
            height = bitmap.height;
            drawSource = bitmap;
        } else {
            const img = new Image();
            objectUrlToRevoke = URL.createObjectURL(file);
            await new Promise((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = (e) => reject(new Error('Failed to load image into memory'));
                img.src = objectUrlToRevoke;
            });
            width = img.naturalWidth || img.width;
            height = img.naturalHeight || img.height;
            drawSource = img;
        }

        if (!width || !height) {
            if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
            if (bitmap && typeof bitmap.close === 'function') bitmap.close();
            return { file, dataUrl: null };
        }

        // Calculate scaled dimensions while preserving aspect ratio
        let targetWidth = width;
        let targetHeight = height;
        if (targetWidth > maxDimension || targetHeight > maxDimension) {
            if (targetWidth > targetHeight) {
                targetHeight = Math.round((targetHeight / targetWidth) * maxDimension);
                targetWidth = maxDimension;
            } else {
                targetWidth = Math.round((targetWidth / targetHeight) * maxDimension);
                targetHeight = maxDimension;
            }
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
            if (bitmap && typeof bitmap.close === 'function') bitmap.close();
            return { file, dataUrl: null };
        }

        // If converting to JPEG, fill white background to avoid black transparency on PNGs with alpha
        if (preferredMime === 'image/jpeg' && (!file.type || file.type.includes('png') || file.type.includes('heic') || file.type.includes('heif'))) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, targetWidth, targetHeight);
        }

        ctx.drawImage(drawSource, 0, 0, targetWidth, targetHeight);

        if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
        if (bitmap && typeof bitmap.close === 'function') bitmap.close();

        // Generate compressed Blob
        const blob = await new Promise((resolve) => {
            canvas.toBlob((b) => resolve(b), preferredMime, quality);
        });

        // Generate clean Data URL for fallback
        const dataUrl = canvas.toDataURL(preferredMime, quality);

        if (blob) {
            const originalName = file.name || 'image.jpg';
            const baseName = originalName.replace(/\.[^/.]+$/, '');
            const ext = blob.type === 'image/webp' ? '.webp' : '.jpg';
            let normalizedFile = blob;
            try {
                normalizedFile = new File([blob], `${baseName}${ext}`, { type: blob.type || preferredMime });
            } catch (e) {
                // If File constructor is not supported in older WebKit, assign name to blob
                blob.name = `${baseName}${ext}`;
                normalizedFile = blob;
            }
            return { file: normalizedFile, dataUrl };
        }

        return { file, dataUrl };
    } catch (err) {
        console.warn('Client-side image normalization skipped (using raw file):', err);
        return { file, dataUrl: null };
    }
};

/**
 * Universal media upload function that handles:
 * 1. Automatic compression / format conversion for iOS HEIC and huge camera photos
 * 2. Demo mode / offline fallback with size-safe Data URLs
 * 3. FormData uploading with proper headers
 * 4. Error fallback to compressed Data URL if server upload fails
 */
export const uploadMediaFile = async (rawFile, options = {}) => {
    if (!rawFile) throw new Error('No file provided');

    const {
        action = 'upload',
        maxDimension = 2560,
        quality = 0.88,
        onProgress = null
    } = options;

    // 1. Prepare and optimize image
    const { file, dataUrl: precalculatedDataUrl } = await normalizeAndCompressImage(rawFile, {
        maxDimension,
        quality
    });

    const isDemo = typeof window !== 'undefined' && (
        localStorage.getItem('omnivaso_demo_mode') === 'true' ||
        window.location.search.includes('demo=true')
    );

    // Fallback Data URL generator
    const getFallbackDataUrl = async () => {
        if (precalculatedDataUrl) return precalculatedDataUrl;
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
        });
    };

    if (isDemo) {
        const dataUrl = await getFallbackDataUrl();
        return { success: true, url: dataUrl, isDataUrl: true };
    }

    const token = localStorage.getItem(getTenantStorageKey('adminToken')) || localStorage.getItem('adminToken') || 'demo-token';
    const fileName = file.name || rawFile.name || 'image.jpg';
    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('token', token);

    try {
        const res = await axios.post(
            getApiUrl(`api.php?action=${action}`),
            formData,
            authHeaders({
                'Content-Type': 'multipart/form-data'
            })
        );

        if (res.data && res.data.success) {
            return { success: true, url: res.data.url, isDataUrl: false };
        }

        console.warn('Server upload returned unsuccessful response, using optimized data URL fallback:', res.data);
        const dataUrl = await getFallbackDataUrl();
        return { success: true, url: dataUrl, isDataUrl: true, serverError: res.data?.message };
    } catch (err) {
        console.warn('Network upload failed, falling back to local optimized data URL:', err);
        const dataUrl = await getFallbackDataUrl();
        if (dataUrl) {
            return { success: true, url: dataUrl, isDataUrl: true, networkError: err.message };
        }
        throw err;
    }
};

/**
 * Fetches existing media list from the uploads directory.
 */
export const fetchMediaLibrary = async () => {
    try {
        const res = await axios.get(
            getApiUrl('api.php?action=media'),
            authHeaders()
        );
        if (res.data && res.data.success && Array.isArray(res.data.files)) {
            return res.data.files;
        }
        return [];
    } catch (err) {
        console.warn('Failed to fetch media library from server:', err);
        return [];
    }
};

/**
 * Deletes a media file from the uploads directory.
 */
export const deleteMediaFile = async (urlOrPath) => {
    if (!urlOrPath) return false;
    try {
        const res = await axios.post(
            getApiUrl('api.php?action=delete_media'),
            { path: urlOrPath },
            authHeaders({ 'Content-Type': 'application/json' })
        );
        return res.data?.success || false;
    } catch (err) {
        console.error('Failed to delete media file:', err);
        return false;
    }
};

